import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { Button, ButtonLink, Card, DataRow, Skeleton, SkeletonGroup } from '@/components/ui';
import { SavedAddresses } from '@/features/account/SavedAddresses';
import { useSession } from '@/features/auth/sessionContext';
import { paths } from '@/routes/paths';

/**
 * Everything a customer can do with their own account, in one place.
 *
 * Before this page there was nowhere to correct a mistyped address: the book
 * was append-only, and the Privacy Policy had to send people to the Grievance
 * Officer for anything self-service would normally cover.
 *
 * The order history is a LINK rather than a copy of the list. Two screens
 * rendering the same orders is two screens to keep in step, and "Your orders"
 * already exists, is linked from the home page, and is where the payment
 * gateway returns people.
 */
export function AccountPage() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  if (session.status === 'loading') {
    return (
      <AppShell title="Your account">
        <SkeletonGroup label="Loading your account">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="mt-3 h-40 w-full" />
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (session.status !== 'authenticated' || !session.user) {
    // `from` so signing in lands them back here rather than on the home page.
    return <Navigate to={paths.signIn} replace state={{ from: location.pathname }} />;
  }

  const { user } = session;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await session.signOut();
      navigate(paths.home);
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <AppShell title="Your account" backTo={paths.home} backLabel="Home">
      <div className="flex flex-col gap-4">
        <Card title="Signed in as">
          <dl>
            {/* The phone is the account: there is no password and no email
                login, so this is the one identifier that matters. Shown in the
                stored E.164 form rather than prettified — it is what a support
                call will be looking for. */}
            <DataRow label="Mobile number" value={user.phone} />
            {user.name ? <DataRow label="Name" value={user.name} /> : null}
          </dl>
        </Card>

        <Card title="Your orders">
          <p className="text-sm text-ink-muted">
            Every order you have placed, with where it has got to.
          </p>
          <ButtonLink to={paths.orders} variant="secondary" className="mt-3">
            View your orders
          </ButtonLink>
        </Card>

        <SavedAddresses />

        <Card title="Sign out">
          <p className="text-sm text-ink-muted">
            You will need a new code from your phone to sign back in. Your orders and addresses stay
            exactly as they are.
          </p>
          <Button
            variant="secondary"
            onClick={() => void handleSignOut()}
            loading={signingOut}
            className="mt-3"
          >
            Sign out
          </Button>
        </Card>
      </div>
    </AppShell>
  );
}
